import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Text,
  BlockStack,
  TextField,
  Select,
  Checkbox,
  Autocomplete,
  Icon,
  InlineStack,
  Button,
  Banner,
} from '@shopify/polaris';
import {
  SearchMinor,
} from '@shopify/polaris-icons';
import { generationScenesService } from '../../services/generationScenesService';
import { generationImportService } from '../../services/generationImportService';
import TextScenePreview from '../text/TextScenePreview';
import TextStyleEditor from '../text/TextStyleEditor';
import {
  buildTextStylePayload,
  normalizeTextStyle,
  toOptionalNumber,
} from '../../utils/textStyleLayout';

const FONT_STYLE_OPTIONS = [
  { label: 'Regular', value: 'regular' },
  { label: 'Italic', value: 'italic' },
  { label: 'Bold', value: 'bold' },
  { label: 'Bold Italic', value: 'bold_italic' },
];

const TEXT_ALIGN_OPTIONS = [
  { label: 'Left', value: 'left' },
  { label: 'Center', value: 'center' },
  { label: 'Right', value: 'right' },
];

const PREVIEW_SOURCE_OPTIONS = [
  { label: 'Online', value: 'online' },
  { label: 'Offline', value: 'offline' },
];

const FONT_STYLE_META = {
  regular: { fontWeight: 400, fontStyle: 'normal', variantKey: 'regular' },
  italic: { fontWeight: 400, fontStyle: 'italic', variantKey: 'italic' },
  bold: { fontWeight: 700, fontStyle: 'normal', variantKey: 'bold' },
  bold_italic: { fontWeight: 700, fontStyle: 'italic', variantKey: 'bold_italic' },
};

const PREVIEW_BASE_WIDTH = 1152;
const PREVIEW_BASE_HEIGHT = 1120;

const normalizeFontStyle = (value) => {
  const key = String(value || 'regular').trim().toLowerCase().replace(/\s+/g, '_');
  return FONT_STYLE_META[key] ? key : 'regular';
};

const normalizeTextAlign = (value) => {
  const key = String(value || 'center').trim().toLowerCase();
  return ['left', 'center', 'right'].includes(key) ? key : 'center';
};

const normalizeColorHex = (value) => {
  const color = String(value || '').trim();
  if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(color)) return color;
  return '#ffffff';
};

const inferFontFormatFromUrl = (url) => {
  const lower = String(url || '').toLowerCase();
  if (lower.includes('.woff2')) return 'woff2';
  if (lower.includes('.woff')) return 'woff';
  if (lower.includes('.otf')) return 'opentype';
  return 'truetype';
};

const isArabicLanguage = (language) => String(language || '').toLowerCase().includes('arabic');

const fileInputStyle = {
  width: '100%',
  border: '1px solid #c9cccf',
  borderRadius: 8,
  padding: '8px 10px',
  fontSize: 13,
};

const EditGenerationSceneModal = ({ 
  isOpen, 
  onClose, 
  selectedScene,
  onSceneUpdated,
  generationStories,
  isLoadingGenerationStories
}) => {
  const [isUpdatingGenerationScene, setIsUpdatingGenerationScene] = useState(false);
  const [editGenerationStoryAutocompleteInputValue, setEditGenerationStoryAutocompleteInputValue] = useState('');
  const [editGenerationStoryAutocompleteOptions, setEditGenerationStoryAutocompleteOptions] = useState([]);
  const [selectedGenerationStoryForEditScene, setSelectedGenerationStoryForEditScene] = useState([]);
  const [availableFonts, setAvailableFonts] = useState([]);
  const [isLoadingFonts, setIsLoadingFonts] = useState(false);
  const [sceneAssetFile, setSceneAssetFile] = useState(null);
  const [maskAssetFile, setMaskAssetFile] = useState(null);
  const [isUploadingSceneAsset, setIsUploadingSceneAsset] = useState(false);
  const [isUploadingMaskAsset, setIsUploadingMaskAsset] = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');
  const [previewKidName, setPreviewKidName] = useState('Kid Name');
  const [isGeneratingTextPreview, setIsGeneratingTextPreview] = useState(false);
  const [generatedTextPreviewUrl, setGeneratedTextPreviewUrl] = useState('');
  const [textPreviewError, setTextPreviewError] = useState('');
  const [previewSource, setPreviewSource] = useState('online');
  const previewHostRef = useRef(null);
  const [previewHostWidth, setPreviewHostWidth] = useState(420);

  const [editGenerationSceneData, setEditGenerationSceneData] = useState({
    slug: '',
    sceneTitle: '',
    order: '',
    type: '',
    scene_url: '',
    scene_url_2: '',
    mask_url: '',
    mask_url_2: '',
    text: '',
    text_overlay_enabled: false,
    scene_prompt: '',
    blurred: false,
    story_id: '',
    demo_text: '',
    use_default_text_style: true,
    text_style_form: normalizeTextStyle({}),
    use_default_cover_style: true,
    cover_font_key: '',
    cover_font_size: '',
    cover_font_style: 'regular',
    cover_text_align: 'center',
    cover_text_x: '',
    cover_text_y: '',
    cover_font_color: '#ffffff',
    cover_rotation: ''
  });

  const fontOptions = [
    { label: 'Language fallback font', value: '' },
    ...availableFonts.map((font) => ({
      label: `${font.label || font.family || font.key} (${font.key})`,
      value: font.key || ''
    }))
  ];

  // Helper function to generate slug from title
  const generateSlug = (title) => {
    if (!title) return '';
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  // Helper function to determine which fields should be shown based on scene type
  const getSceneTypeFields = (sceneType) => {
    switch (sceneType) {
      case 'cover':
        return {
          showText: true,
          showMaskUrl: true,
          showBlurred: false,
          showDemoText: true,
          showPrompt: true,
          textRequired: false,
          maskUrlRequired: false,
          demoTextRequired: false
        };
      case 'image':
        return {
          showText: false,
          showMaskUrl: true,
          showBlurred: false,
          showDemoText: false,
          showPrompt: true,
          textRequired: false,
          maskUrlRequired: false,
          demoTextRequired: false
        };
      case 'text':
        return {
          showText: true,
          showMaskUrl: false,
          showBlurred: false,
          showDemoText: false,
          showPrompt: false,
          textRequired: true,
          maskUrlRequired: false,
          demoTextRequired: false
        };
      case 'static':
        return {
          showText: false,
          showMaskUrl: false,
          showBlurred: true,
          showDemoText: false,
          showPrompt: false,
          textRequired: false,
          maskUrlRequired: false,
          demoTextRequired: false
        };
      case 'back':
        return {
          showText: false,
          showMaskUrl: false,
          showBlurred: false,
          showDemoText: false,
          showPrompt: false,
          textRequired: false,
          maskUrlRequired: false,
          demoTextRequired: false
        };
      default:
        return {
          showText: false,
          showMaskUrl: false,
          showBlurred: false,
          showDemoText: false,
          showPrompt: false,
          textRequired: false,
          maskUrlRequired: false,
          demoTextRequired: false
        };
    }
  };

  // Helper function to prepare scene data based on scene type
  const buildTextStyleOverridePayload = (sceneData) => {
    if (!['text', 'image'].includes(sceneData.type)) return null;
    if (sceneData.use_default_text_style) {
      return { use_default: true };
    }
    return buildTextStylePayload(sceneData.text_style_form, { useDefault: false });
  };

  const buildCoverStyleOverridePayload = (sceneData) => {
    if (sceneData.type !== 'cover') return null;
    if (sceneData.use_default_cover_style) {
      return { use_default: true };
    }
    return {
      use_default: false,
      font_key: sceneData.cover_font_key || '',
      font_size: toOptionalNumber(sceneData.cover_font_size),
      font_style: sceneData.cover_font_style || 'regular',
      text_align: sceneData.cover_text_align || 'center',
      x: toOptionalNumber(sceneData.cover_text_x),
      y: toOptionalNumber(sceneData.cover_text_y),
      color: normalizeColorHex(sceneData.cover_font_color),
      rotation: toOptionalNumber(sceneData.cover_rotation) ?? 0,
    };
  };

  const prepareSceneData = (sceneData) => {
    const textStyleOverride = buildTextStyleOverridePayload(sceneData);
    const coverStyleOverride = buildCoverStyleOverridePayload(sceneData);

    const baseData = {
      slug: sceneData.slug,
      order: sceneData.order,
      type: sceneData.type,
      scene_url: sceneData.scene_url || sceneData.scene_url_2 || '',
      scene_url_2: sceneData.scene_url_2 || sceneData.scene_url || '',
      story_id: sceneData.story_id,
      text_style_override: textStyleOverride,
      cover_style_override: coverStyleOverride,
    };

    switch (sceneData.type) {
      case 'cover':
        return {
          ...baseData,
          mask_url: sceneData.mask_url,
          mask_url_2: sceneData.mask_url_2 || sceneData.mask_url,
          text: sceneData.text,
          demo_text: sceneData.demo_text,
          scene_prompt: sceneData.scene_prompt,
          ...(isSpreadStory ? { cover_wrap: sceneData.cover_wrap === true && String(sceneData.order) === '0' } : {})
        };
      case 'image':
        return {
          ...baseData,
          mask_url: sceneData.mask_url,
          mask_url_2: sceneData.mask_url_2 || sceneData.mask_url,
          text_overlay_enabled: sceneData.text_overlay_enabled === true,
          text: String(sceneData.text || ''),
          scene_prompt: sceneData.scene_prompt,
          ...(isSpreadStory ? {
            left_text: String(sceneData.left_text || ''),
            right_text: String(sceneData.right_text || '')
          } : {})
        };
      case 'text':
        return {
          ...baseData,
          text: sceneData.text
        };
      case 'static':
        return {
          ...baseData,
          blurred: sceneData.blurred
        };
      case 'back':
        return {
          ...baseData
        };
      default:
        return baseData;
    }
  };

  // Initialize form data when selectedScene changes
  useEffect(() => {
    if (selectedScene && isOpen) {
      const sceneOverride = selectedScene.text_style_override || null;
      const useDefaultTextStyle = !sceneOverride || sceneOverride.use_default !== false;
      const coverOverride = selectedScene.cover_style_override || null;
      const useDefaultCoverStyle = !coverOverride || coverOverride.use_default !== false;
      setEditGenerationSceneData({
        ...selectedScene,
        sceneTitle: selectedScene.sceneTitle || (selectedScene.slug ? selectedScene.slug.replace(/-/g, ' ') : ''),
        scene_url: selectedScene.scene_url || '',
        scene_url_2: selectedScene.scene_url_2 || selectedScene.scene_url || '',
        mask_url: selectedScene.mask_url || '',
        mask_url_2: selectedScene.mask_url_2 || selectedScene.mask_url || '',
        text: String(selectedScene.text || ''),
        text_overlay_enabled: selectedScene.text_overlay_enabled === true,
        use_default_text_style: useDefaultTextStyle,
        text_style_form: normalizeTextStyle(sceneOverride || {}),
        use_default_cover_style: useDefaultCoverStyle,
        cover_font_key: coverOverride?.font_key || '',
        cover_font_size: coverOverride?.font_size ?? '',
        cover_font_style: coverOverride?.font_style || 'regular',
        cover_text_align: coverOverride?.text_align || coverOverride?.textAlign || 'center',
        cover_text_x: coverOverride?.x ?? '',
        cover_text_y: coverOverride?.y ?? '',
        cover_font_color: coverOverride?.color || '#ffffff',
        cover_rotation: coverOverride?.rotation ?? ''
      });
      setSelectedGenerationStoryForEditScene(selectedScene.story_id ? [selectedScene.story_id] : []);
      const selectedStory = generationStories.find(s => s._id === selectedScene.story_id);
      setEditGenerationStoryAutocompleteInputValue(selectedStory ? selectedStory.title || '' : '');
      setSceneAssetFile(null);
      setMaskAssetFile(null);
      setActionError('');
      setActionSuccess('');
      setPreviewKidName('Kid Name');
      setTextPreviewError('');
      setPreviewSource('online');
      setGeneratedTextPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return '';
      });
    }
  }, [selectedScene, isOpen, generationStories]);

  useEffect(() => {
    if (!isOpen) {
      setSceneAssetFile(null);
      setMaskAssetFile(null);
      setActionError('');
      setActionSuccess('');
      setIsUploadingSceneAsset(false);
      setIsUploadingMaskAsset(false);
      setIsUpdatingGenerationScene(false);
      setPreviewKidName('Kid Name');
      setTextPreviewError('');
      setIsGeneratingTextPreview(false);
      setPreviewSource('online');
      setGeneratedTextPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return '';
      });
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (generatedTextPreviewUrl) {
        URL.revokeObjectURL(generatedTextPreviewUrl);
      }
    };
  }, [generatedTextPreviewUrl]);

  useEffect(() => {
    if (!isOpen || !previewHostRef.current) return undefined;

    const element = previewHostRef.current;
    const updateSize = (width) => {
      if (!Number.isFinite(width) || width <= 0) return;
      setPreviewHostWidth(width);
    };

    updateSize(element.getBoundingClientRect().width);

    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      if (!entries || !entries.length) return;
      updateSize(entries[0].contentRect.width);
    });
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    let isMounted = true;
    const loadFonts = async () => {
      setIsLoadingFonts(true);
      try {
        const result = await generationImportService.getFonts({ activeOnly: true });
        if (isMounted) {
          setAvailableFonts(Array.isArray(result?.data) ? result.data : []);
        }
      } catch (err) {
        if (isMounted) {
          setAvailableFonts([]);
        }
      } finally {
        if (isMounted) {
          setIsLoadingFonts(false);
        }
      }
    };

    loadFonts();
    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  // Update autocomplete options when stories change
  useEffect(() => {
    const options = generationStories.map(story => ({ label: story.title || '', value: story._id }));
    setEditGenerationStoryAutocompleteOptions(options);
  }, [generationStories]);

  // Generation Scene autocomplete handlers
  const handleEditGenerationStoryAutocompleteChange = (value) => {
    setEditGenerationStoryAutocompleteInputValue(value);
    if (value === '') {
      const allOptions = generationStories.map(story => ({ label: story.title || '', value: story._id }));
      setEditGenerationStoryAutocompleteOptions(allOptions);
      return;
    }
    const filterRegex = new RegExp(value, 'i');
    const filteredOptions = generationStories
      .filter(story => (story.title && story.title.match(filterRegex)) || (story.slug && story.slug.match(filterRegex)))
      .map(story => ({ label: story.title || '', value: story._id }));
    setEditGenerationStoryAutocompleteOptions(filteredOptions);
  };

  const handleEditGenerationStoryAutocompleteSelect = (selected) => {
    setSelectedGenerationStoryForEditScene(selected);
    const selectedId = selected[0] || '';
    setEditGenerationSceneData(prev => ({ ...prev, story_id: selectedId }));
    const selectedStory = generationStories.find(story => story._id === selectedId);
    setEditGenerationStoryAutocompleteInputValue(selectedStory ? selectedStory.title || '' : '');
  };

  const getCurrentStorySlug = () => {
    const story = generationStories.find((s) => s._id === editGenerationSceneData.story_id);
    return story?.slug || '';
  };

  const handleUploadAsset = async (assetKind) => {
    const isMask = assetKind === 'mask';
    const file = isMask ? maskAssetFile : sceneAssetFile;
    if (!file) return;

    const setUploading = isMask ? setIsUploadingMaskAsset : setIsUploadingSceneAsset;
    const setFile = isMask ? setMaskAssetFile : setSceneAssetFile;

    setUploading(true);
    setActionError('');
    setActionSuccess('');
    try {
      const result = await generationScenesService.uploadSceneAsset({
        file,
        assetKind,
        storySlug: getCurrentStorySlug(),
        sceneSlug: editGenerationSceneData.slug,
        order: editGenerationSceneData.order,
      });

      const uploadedUrl = result?.data?.url || '';
      if (!uploadedUrl) {
        throw new Error('Upload succeeded but no URL returned');
      }

      const patchData = isMask
        ? { mask_url: uploadedUrl, mask_url_2: uploadedUrl }
        : { scene_url: uploadedUrl, scene_url_2: uploadedUrl };

      if (selectedScene?._id) {
        await generationScenesService.updateScene(selectedScene._id, patchData);
      }

      setEditGenerationSceneData((prev) => ({
        ...prev,
        ...patchData,
      }));

      if (onSceneUpdated) {
        onSceneUpdated();
      }

      setFile(null);
      setActionSuccess(`${isMask ? 'Mask' : 'Scene'} image uploaded and applied successfully`);
    } catch (err) {
      setActionError(err.message || `Failed to upload ${isMask ? 'mask' : 'scene'} image`);
    } finally {
      setUploading(false);
    }
  };

  const editSceneStory = generationStories.find(
    (story) => story._id === selectedScene?.story_id
  );
  const isPromptStory = editSceneStory?.comfy_flow === 'prompt_story';
  const isSpreadStory = editSceneStory?.layout_mode === 'spread';

  const handleUpdateGenerationScene = async () => {
    const typeFields = getSceneTypeFields(editGenerationSceneData.type);
    const missingFields = [];
    const hasPrompt = Boolean(String(editGenerationSceneData.scene_prompt || '').trim());
    const hasSceneUrl = Boolean(String(editGenerationSceneData.scene_url || editGenerationSceneData.scene_url_2 || '').trim());
    const hasMaskUrl = Boolean(String(editGenerationSceneData.mask_url || editGenerationSceneData.mask_url_2 || '').trim());

    if (!selectedScene) missingFields.push('Scene');
    if (!editGenerationSceneData.slug) missingFields.push('Scene Slug');
    if (!editGenerationSceneData.type) missingFields.push('Scene Type');
    if (!editGenerationSceneData.story_id) missingFields.push('Story');
    if (isPromptStory && editGenerationSceneData.type !== 'back') {
      if (!hasPrompt) missingFields.push('Scene Prompt');
    } else if (editGenerationSceneData.type === 'back') {
      if (!hasSceneUrl) missingFields.push('Scene URL');
    } else if (!hasSceneUrl && !hasPrompt) {
      missingFields.push('Scene URL or Scene Prompt');
    }
    if (typeFields.textRequired && !String(editGenerationSceneData.text || '').trim()) missingFields.push('Scene Text');
    if (
      editGenerationSceneData.type === 'image' &&
      editGenerationSceneData.text_overlay_enabled
    ) {
      if (isSpreadStory) {
        if (!String(editGenerationSceneData.left_text || '').trim() && !String(editGenerationSceneData.right_text || '').trim()) {
          missingFields.push('Left or Right Page Text');
        }
      } else if (!String(editGenerationSceneData.text || '').trim()) {
        missingFields.push('Scene Text');
      }
    }
    if (typeFields.maskUrlRequired && !isPromptStory && !hasMaskUrl && !hasPrompt) missingFields.push('Mask URL');
    if (typeFields.demoTextRequired && !String(editGenerationSceneData.demo_text || '').trim()) missingFields.push('Demo Text');

    if (missingFields.length > 0) {
      setActionError(`Please fill required fields: ${missingFields.join(', ')}`);
      return;
    }

    setIsUpdatingGenerationScene(true);
    setActionError('');
    setActionSuccess('');
    try {
      const preparedData = prepareSceneData(editGenerationSceneData);
      console.log('Updating generation scene data:', preparedData);
      await generationScenesService.updateScene(selectedScene._id, preparedData);
      
      onClose();
      
      // Notify parent component to refresh scenes list
      if (onSceneUpdated) {
        onSceneUpdated();
      }
    } catch (err) {
      console.error('Error updating generation scene:', err);
      setActionError(err.message || 'Failed to update scene');
    } finally {
      setIsUpdatingGenerationScene(false);
    }
  };

  const handleTestGenerateScenePreview = async () => {
    const isTextScene = editGenerationSceneData.type === 'text' ||
      (editGenerationSceneData.type === 'image' && editGenerationSceneData.text_overlay_enabled);
    const isCoverScene = editGenerationSceneData.type === 'cover';
    if (!isTextScene && !isCoverScene) return;

    const sourceUrl = editGenerationSceneData.scene_url_2 || editGenerationSceneData.scene_url;
    if (!editGenerationSceneData.story_id || !sourceUrl) {
      setTextPreviewError('Story and scene URL are required to generate preview');
      return;
    }

    setIsGeneratingTextPreview(true);
    setTextPreviewError('');
    try {
      const payload = {
        story_id: editGenerationSceneData.story_id,
        scene_id: selectedScene?._id,
        order: editGenerationSceneData.order,
        scene_url: editGenerationSceneData.scene_url,
        scene_url_2: editGenerationSceneData.scene_url_2 || editGenerationSceneData.scene_url,
        kid_name: previewKidName || 'Kid Name',
        source: previewSource,
      };

      let blob;
      if (isCoverScene) {
        blob = await generationScenesService.renderCoverScenePreview({
          ...payload,
          cover_style_override: buildCoverStyleOverridePayload(editGenerationSceneData),
        });
      } else {
        blob = await generationScenesService.renderTextScenePreview({
          ...payload,
          text: editGenerationSceneData.text,
          text_style_override: buildTextStyleOverridePayload(editGenerationSceneData),
        });
      }

      const objectUrl = URL.createObjectURL(blob);
      setGeneratedTextPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return objectUrl;
      });
    } catch (err) {
      setTextPreviewError(err.message || 'Failed to generate text preview');
    } finally {
      setIsGeneratingTextPreview(false);
    }
  };

  if (!selectedScene) return null;

  const selectedStory = generationStories.find((story) => story._id === editGenerationSceneData.story_id) || null;

  const storyLanguage = selectedStory?.language || 'english';
  const storyDefaultStyle = selectedStory?.text_default_style || null;
  const isTextPreviewScene = editGenerationSceneData.type === 'text' ||
    (editGenerationSceneData.type === 'image' && editGenerationSceneData.text_overlay_enabled);
  const isCoverPreviewScene = editGenerationSceneData.type === 'cover';
  const isTextLikePreviewScene = isTextPreviewScene || isCoverPreviewScene;

  let resolvedPreviewStyle = null;
  if (isTextPreviewScene) {
    if (editGenerationSceneData.use_default_text_style) {
      resolvedPreviewStyle = storyDefaultStyle || null;
    } else {
      resolvedPreviewStyle = normalizeTextStyle(editGenerationSceneData.text_style_form);
    }
  } else if (isCoverPreviewScene) {
    if (editGenerationSceneData.use_default_cover_style) {
      resolvedPreviewStyle = null;
    } else {
      resolvedPreviewStyle = {
        use_default: false,
        font_key: editGenerationSceneData.cover_font_key || '',
        font_size: toOptionalNumber(editGenerationSceneData.cover_font_size),
        font_style: editGenerationSceneData.cover_font_style || 'regular',
        text_align: editGenerationSceneData.cover_text_align || 'center',
        x: toOptionalNumber(editGenerationSceneData.cover_text_x),
        y: toOptionalNumber(editGenerationSceneData.cover_text_y),
        color: normalizeColorHex(editGenerationSceneData.cover_font_color),
        rotation: toOptionalNumber(editGenerationSceneData.cover_rotation) || 0,
      };
    }
  }

  const fontLibraryByKey = new Map();
  availableFonts.forEach((font) => {
    const key = String(font?.key || '').trim().toLowerCase();
    if (!key) return;
    fontLibraryByKey.set(key, font);
  });

  const previewFontStyle = normalizeFontStyle(resolvedPreviewStyle?.font_style || 'regular');
  const previewFontMeta = FONT_STYLE_META[previewFontStyle] || FONT_STYLE_META.regular;
  const previewFontKey = String(resolvedPreviewStyle?.font_key || '').trim().toLowerCase();
  const previewFontRecord = previewFontKey ? fontLibraryByKey.get(previewFontKey) : null;
  const previewFontVariantUrl = previewFontRecord
    ? (previewFontRecord?.variants?.[previewFontMeta.variantKey] || previewFontRecord?.variants?.regular || '')
    : '';

  const fallbackFamily = isArabicLanguage(storyLanguage) ? 'PingARLT' : 'PingLCG';
  const previewFontFamilyAlias = previewFontRecord
    ? `GenerationPreview_${String(previewFontRecord.key || 'font').replace(/[^a-zA-Z0-9_-]/g, '_')}_${previewFontStyle}`
    : fallbackFamily;

  const previewFontFaceCss = previewFontVariantUrl
    ? `@font-face { font-family: '${previewFontFamilyAlias}'; src: url('${previewFontVariantUrl}') format('${inferFontFormatFromUrl(previewFontVariantUrl)}'); font-weight: ${previewFontMeta.fontWeight}; font-style: ${previewFontMeta.fontStyle}; font-display: swap; }`
    : '';

  const previewTextRaw = isCoverPreviewScene
    ? String(previewKidName || 'Kid Name')
    : String(editGenerationSceneData.text || '');
  const previewText = previewTextRaw.replace(/\{\{Kid_Name\}\}/g, previewKidName || 'Kid Name');
  const previewDirection = isArabicLanguage(storyLanguage) ? 'rtl' : 'ltr';
  const previewTextAlign = normalizeTextAlign(resolvedPreviewStyle?.text_align || resolvedPreviewStyle?.textAlign);
  const previewTextX = toOptionalNumber(resolvedPreviewStyle?.x);
  const previewTextY = toOptionalNumber(resolvedPreviewStyle?.y);
  const previewFontSize = toOptionalNumber(resolvedPreviewStyle?.font_size) || 72;
  const previewFontColor = normalizeColorHex(resolvedPreviewStyle?.color || '#ffffff');
  const previewRotation = toOptionalNumber(resolvedPreviewStyle?.rotation) || 0;
  const previewSceneImageUrl = previewSource === 'offline'
    ? (editGenerationSceneData.scene_url_2 || editGenerationSceneData.scene_url)
    : (editGenerationSceneData.scene_url || editGenerationSceneData.scene_url_2);
  const previewScale = Math.max(0.2, Number(previewHostWidth || 420) / PREVIEW_BASE_WIDTH);
  const canGenerateScenePreview = Boolean(
    isTextLikePreviewScene &&
    editGenerationSceneData.story_id &&
    previewSceneImageUrl
  );

  const previewOverlayStyle = (() => {
    const withRotation = (baseTransform) => `${baseTransform} rotate(${previewRotation}deg)`;
    const shared = {
      fontFamily: `'${previewFontFamilyAlias}', '${fallbackFamily}', Arial, sans-serif`,
      fontWeight: previewFontMeta.fontWeight,
      fontStyle: previewFontMeta.fontStyle,
      fontSize: `${previewFontSize}px`,
      color: previewFontColor,
      textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
      textAlign: previewTextAlign,
      direction: previewDirection,
      lineHeight: 1.4,
      letterSpacing: '0px',
      whiteSpace: 'pre-wrap',
      wordWrap: 'break-word',
      zIndex: 2,
      position: 'absolute',
    };

    const overlayWidth = PREVIEW_BASE_WIDTH - 100;
    const normalOverlayWidth = Math.floor(PREVIEW_BASE_WIDTH * 0.7);

    if (previewTextX !== null && previewTextY !== null) {
      return {
        ...shared,
        left: `${previewTextX}px`,
        top: `${previewTextY}px`,
        width: `${overlayWidth}px`,
        transform: withRotation('translateX(-50%)'),
      };
    }

    if (previewTextX === null && previewTextY !== null) {
      return {
        ...shared,
        left: '50%',
        top: `${previewTextY}px`,
        width: `${overlayWidth}px`,
        transform: withRotation('translateX(-50%)'),
      };
    }

    if (previewTextX !== null && previewTextY === null) {
      return {
        ...shared,
        left: `${previewTextX}px`,
        top: '50%',
        width: `${overlayWidth}px`,
        transform: withRotation('translate(-50%, -50%)'),
      };
    }

    return {
      ...shared,
      left: '50%',
      top: '50%',
      width: `${normalOverlayWidth}px`,
      transform: withRotation('translate(-50%, -50%)'),
      padding: '0 24px',
    };
  })();

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={<div style={{padding: '16px'}}>{'Edit Generation Scene'}</div>}
      primaryAction={{
        content: 'Save',
        onAction: handleUpdateGenerationScene,
        loading: isUpdatingGenerationScene,
        disabled: isUploadingSceneAsset || isUploadingMaskAsset,
      }}
      secondaryActions={[
        {
          content: 'Cancel',
          onAction: onClose,
          disabled: isUpdatingGenerationScene || isUploadingSceneAsset || isUploadingMaskAsset,
        },
      ]}
      footer={<div style={{padding: '16px'}}></div>}
    >
      <BlockStack gap="400">
        {actionError ? (
          <Banner tone="critical">
            <Text as="p">{actionError}</Text>
          </Banner>
        ) : null}

        {actionSuccess ? (
          <Banner tone="success">
            <Text as="p">{actionSuccess}</Text>
          </Banner>
        ) : null}

        <TextField
          label="Scene Title"
          value={editGenerationSceneData.sceneTitle}
          onChange={(value) => setEditGenerationSceneData(prev => ({ ...prev, sceneTitle: value, slug: generateSlug(value) }))}
          placeholder="Scene title"
          required
        />
        <TextField
          label="Scene Slug"
          value={editGenerationSceneData.slug}
          onChange={() => {}}
          required
          disabled
        />
        <TextField
          label="Order"
          type="number"
          value={editGenerationSceneData.order}
          onChange={(value) => setEditGenerationSceneData(prev => ({ ...prev, order: parseInt(value) || 0 }))}
          placeholder="0"
          min="0"
          required
        />
        <Select
          label="Scene Type"
          options={[
            { label: 'Select Scene Type', value: '', disabled: true },
            { label: 'Cover', value: 'cover' },
            { label: 'Image', value: 'image' },
            { label: 'Text', value: 'text' },
            { label: 'Static', value: 'static' },
            { label: 'Back', value: 'back' },
          ]}
          onChange={(value) => setEditGenerationSceneData(prev => ({ ...prev, type: value }))}
          value={editGenerationSceneData.type}
          required
        />
        <Autocomplete
          options={editGenerationStoryAutocompleteOptions}
          selected={selectedGenerationStoryForEditScene}
          onSelect={handleEditGenerationStoryAutocompleteSelect}
          loading={isLoadingGenerationStories}
          textField={
            <Autocomplete.TextField
              onChange={handleEditGenerationStoryAutocompleteChange}
              label="Story"
              value={editGenerationStoryAutocompleteInputValue}
              prefix={<Icon source={SearchMinor} tone="base" />}
              placeholder="Search for a story..."
              autoComplete="off"
              required
            />
          }
        />
        {(!isPromptStory || editGenerationSceneData.type === 'back') && (
          <TextField
            label="Scene URL"
            value={editGenerationSceneData.scene_url}
            onChange={(value) => setEditGenerationSceneData(prev => ({ ...prev, scene_url: value, scene_url_2: value }))}
            placeholder="https://example.com/scene.png"
            required
          />
        )}
        {(!isPromptStory || editGenerationSceneData.type === 'back') && (
        <div style={{ border: '1px solid #e3e3e3', borderRadius: 8, padding: 12 }}>
          <BlockStack gap="200">
            <Text as="p" variant="bodySm" tone="subdued">Replace Scene Image (uploads to Azure and updates Scene URL)</Text>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(event) => {
                const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
                setSceneAssetFile(file);
              }}
              style={fileInputStyle}
            />
            <InlineStack gap="200" align="space-between">
              <Text as="p" variant="bodySm" tone="subdued">
                {sceneAssetFile ? `Selected: ${sceneAssetFile.name}` : 'No file selected'}
              </Text>
              <Button
                size="slim"
                onClick={() => handleUploadAsset('scene')}
                loading={isUploadingSceneAsset}
                disabled={!sceneAssetFile || isUploadingMaskAsset || isUpdatingGenerationScene}
              >
                Upload Scene Image
              </Button>
            </InlineStack>
          </BlockStack>
        </div>
        )}
        {(getSceneTypeFields(editGenerationSceneData.type).showPrompt || (isPromptStory && editGenerationSceneData.type !== 'back')) && (
          <TextField
            label="Scene Prompt"
            value={editGenerationSceneData.scene_prompt}
            onChange={(value) => setEditGenerationSceneData(prev => ({ ...prev, scene_prompt: value }))}
            placeholder="Prompt used to generate this scene..."
            multiline={4}
            helpText="Optional. Stored as multiline string in database."
          />
        )}
        {editGenerationSceneData.type === 'image' ? (
          <Checkbox
            label="Enable text overlay"
            checked={editGenerationSceneData.text_overlay_enabled}
            onChange={(checked) => setEditGenerationSceneData(prev => ({ ...prev, text_overlay_enabled: checked }))}
          />
        ) : null}
        {isSpreadStory && editGenerationSceneData.type === 'image' && editGenerationSceneData.text_overlay_enabled ? (
          <BlockStack gap="200">
            <TextField
              label="Left Page Text"
              value={editGenerationSceneData.left_text || ''}
              onChange={(value) => setEditGenerationSceneData(prev => ({ ...prev, left_text: value }))}
              placeholder="Text drawn on the left page..."
              multiline={2}
            />
            <TextField
              label="Right Page Text"
              value={editGenerationSceneData.right_text || ''}
              onChange={(value) => setEditGenerationSceneData(prev => ({ ...prev, right_text: value }))}
              placeholder="Text drawn on the right page..."
              multiline={2}
            />
            <Text variant="bodySm" tone="subdued">
              At least one page text is required. Use {'{{Kid_Name}}'} for the kid name. Style and placement apply per page.
            </Text>
          </BlockStack>
        ) : null}
        {isSpreadStory && editGenerationSceneData.type === 'cover' && String(editGenerationSceneData.order) === '0' ? (
          <Checkbox
            label="Cover wrap (left half = back cover, right half = front cover)"
            checked={editGenerationSceneData.cover_wrap === true}
            onChange={(checked) => setEditGenerationSceneData(prev => ({ ...prev, cover_wrap: checked }))}
          />
        ) : null}
        {(getSceneTypeFields(editGenerationSceneData.type).showText ||
          (editGenerationSceneData.type === 'image' && editGenerationSceneData.text_overlay_enabled)) && (
          <div>
            {!(isSpreadStory && editGenerationSceneData.type === 'image') ? (
            <TextField
              label="Scene Text"
              value={editGenerationSceneData.text}
              onChange={(value) => setEditGenerationSceneData(prev => ({ ...prev, text: value }))}
              placeholder="Scene text..."
              multiline={2}
              required={getSceneTypeFields(editGenerationSceneData.type).textRequired}
            />
            ) : null}
            {['text', 'image'].includes(editGenerationSceneData.type) && !(isSpreadStory && editGenerationSceneData.type === 'image') ? (
              <Text variant="bodySm" tone="subdued" style={{ marginTop: '4px' }}>
                Use {'{{Kid_Name}}'} for Kid name variable
              </Text>
            ) : null}

            {editGenerationSceneData.type === 'cover' ? (
              <Text variant="bodySm" tone="subdued" style={{ marginTop: '4px' }}>
                Cover generation overlays the kid name directly. Scene text is kept for metadata only.
              </Text>
            ) : null}

            {['text', 'image'].includes(editGenerationSceneData.type) ? (
              <div style={{ marginTop: 12, border: '1px solid #e3e3e3', borderRadius: 8, padding: 12 }}>
                <BlockStack gap="300">
                  <Checkbox
                    label="Use story default text style"
                    checked={editGenerationSceneData.use_default_text_style}
                    onChange={(checked) => setEditGenerationSceneData(prev => ({ ...prev, use_default_text_style: checked }))}
                  />

                  {!editGenerationSceneData.use_default_text_style ? (
                    <TextStyleEditor
                      value={editGenerationSceneData.text_style_form}
                      onChange={(textStyle) => setEditGenerationSceneData((previous) => ({
                        ...previous,
                        text_style_form: textStyle,
                      }))}
                      fontOptions={fontOptions}
                      isLoadingFonts={isLoadingFonts}
                    />
                  ) : null}
                </BlockStack>
              </div>
            ) : null}

            {editGenerationSceneData.type === 'cover' ? (
              <div style={{ marginTop: 12, border: '1px solid #e3e3e3', borderRadius: 8, padding: 12 }}>
                <BlockStack gap="300">
                  <Checkbox
                    label="Use story default cover style"
                    checked={editGenerationSceneData.use_default_cover_style}
                    onChange={(checked) => setEditGenerationSceneData(prev => ({ ...prev, use_default_cover_style: checked }))}
                  />

                  {!editGenerationSceneData.use_default_cover_style ? (
                    <>
                      <Select
                        label="Font"
                        options={fontOptions}
                        value={editGenerationSceneData.cover_font_key}
                        onChange={(value) => setEditGenerationSceneData(prev => ({ ...prev, cover_font_key: value }))}
                        helpText={isLoadingFonts ? 'Loading font library...' : 'Optional. If empty, renderer uses language fallback font.'}
                      />

                      <InlineStack gap="300" wrap>
                        <div style={{ minWidth: 180, flex: 1 }}>
                          <TextField
                            label="Font Size"
                            type="number"
                            value={String(editGenerationSceneData.cover_font_size)}
                            onChange={(value) => setEditGenerationSceneData(prev => ({ ...prev, cover_font_size: value }))}
                            autoComplete="off"
                            placeholder="72"
                          />
                        </div>
                        <div style={{ minWidth: 180, flex: 1 }}>
                          <Select
                            label="Font Style"
                            options={FONT_STYLE_OPTIONS}
                            value={editGenerationSceneData.cover_font_style}
                            onChange={(value) => setEditGenerationSceneData(prev => ({ ...prev, cover_font_style: value }))}
                          />
                        </div>
                        <div style={{ minWidth: 180, flex: 1 }}>
                          <Select
                            label="Text Align"
                            options={TEXT_ALIGN_OPTIONS}
                            value={editGenerationSceneData.cover_text_align}
                            onChange={(value) => setEditGenerationSceneData(prev => ({ ...prev, cover_text_align: value }))}
                          />
                        </div>
                      </InlineStack>

                      <InlineStack gap="300" wrap>
                        <div style={{ minWidth: 180, flex: 1 }}>
                          <TextField
                            label="Text X"
                            type="number"
                            value={String(editGenerationSceneData.cover_text_x)}
                            onChange={(value) => setEditGenerationSceneData(prev => ({ ...prev, cover_text_x: value }))}
                            autoComplete="off"
                            placeholder="865"
                          />
                        </div>
                        <div style={{ minWidth: 180, flex: 1 }}>
                          <TextField
                            label="Text Y"
                            type="number"
                            value={String(editGenerationSceneData.cover_text_y)}
                            onChange={(value) => setEditGenerationSceneData(prev => ({ ...prev, cover_text_y: value }))}
                            autoComplete="off"
                            placeholder="2215"
                          />
                        </div>
                      </InlineStack>

                      <InlineStack gap="300" wrap>
                        <div style={{ minWidth: 180, flex: 1 }}>
                          <TextField
                            label="Font Color"
                            value={String(editGenerationSceneData.cover_font_color)}
                            onChange={(value) => setEditGenerationSceneData(prev => ({ ...prev, cover_font_color: value }))}
                            autoComplete="off"
                            placeholder="#ffffff"
                          />
                        </div>
                        <div style={{ minWidth: 180, flex: 1 }}>
                          <TextField
                            label="Rotation Angle"
                            type="number"
                            value={String(editGenerationSceneData.cover_rotation)}
                            onChange={(value) => setEditGenerationSceneData(prev => ({ ...prev, cover_rotation: value }))}
                            autoComplete="off"
                            placeholder="0"
                          />
                        </div>
                      </InlineStack>
                    </>
                  ) : null}
                </BlockStack>
              </div>
            ) : null}

            {isTextLikePreviewScene ? (
              <div style={{ marginTop: 12, border: '1px solid #e3e3e3', borderRadius: 8, padding: 12, background: '#fafafa' }}>
                <BlockStack gap="200">
                  <Text as="p" variant="headingSm">Live {isCoverPreviewScene ? 'Cover' : 'Text'} Preview</Text>
                  <TextField
                    label="Preview Kid Name"
                    value={previewKidName}
                    onChange={setPreviewKidName}
                    autoComplete="off"
                    placeholder="Kid Name"
                    helpText={isCoverPreviewScene ? 'Used as the rendered cover name.' : 'Used only for preview replacement of {{Kid_Name}}'}
                  />
                  <Select
                    label="Preview Source"
                    options={PREVIEW_SOURCE_OPTIONS}
                    value={previewSource}
                    onChange={setPreviewSource}
                    helpText="Choose the same source as real generation to match config behavior."
                  />
                  <Text as="p" variant="bodySm" tone="subdued">
                    Preview uses current unsaved values and updates in real time. Changes are persisted only after Save.
                  </Text>

                  <InlineStack align="space-between" blockAlign="center">
                    <Text as="p" variant="bodySm" tone="subdued">
                      Test generate uses backend renderer (same generation pipeline behavior).
                    </Text>
                    <Button
                      size="slim"
                      onClick={handleTestGenerateScenePreview}
                      loading={isGeneratingTextPreview}
                      disabled={!canGenerateScenePreview || isUpdatingGenerationScene}
                    >
                      Test Generate
                    </Button>
                  </InlineStack>

                  {textPreviewError ? (
                    <Banner tone="critical">
                      <Text as="p">{textPreviewError}</Text>
                    </Banner>
                  ) : null}

                  {isTextPreviewScene ? (
                    <TextScenePreview
                      imageUrl={previewSceneImageUrl}
                      text={editGenerationSceneData.text}
                      kidName={previewKidName}
                      language={storyLanguage}
                      order={editGenerationSceneData.order}
                      source={previewSource}
                      style={resolvedPreviewStyle}
                      fonts={availableFonts}
                      generatedPreviewUrl={generatedTextPreviewUrl}
                    />
                  ) : (
                    <>
                      {previewFontFaceCss ? <style>{previewFontFaceCss}</style> : null}
                      <div ref={previewHostRef} style={{ width: '100%', maxWidth: 420 }}>
                        <div
                          style={{
                            position: 'relative',
                            width: '100%',
                            paddingTop: `${(PREVIEW_BASE_HEIGHT / PREVIEW_BASE_WIDTH) * 100}%`,
                            borderRadius: 8,
                            overflow: 'hidden',
                            background: '#f1f2f3',
                            border: '1px solid #d8dbde',
                          }}
                        >
                          <div
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: `${PREVIEW_BASE_WIDTH}px`,
                              height: `${PREVIEW_BASE_HEIGHT}px`,
                              transform: `scale(${previewScale})`,
                              transformOrigin: 'top left',
                            }}
                          >
                            {previewSceneImageUrl ? (
                              <img
                                src={previewSceneImageUrl}
                                alt="Scene preview"
                                style={{
                                  width: `${PREVIEW_BASE_WIDTH}px`,
                                  height: `${PREVIEW_BASE_HEIGHT}px`,
                                  objectFit: 'cover',
                                  display: 'block',
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: `${PREVIEW_BASE_WIDTH}px`,
                                  height: `${PREVIEW_BASE_HEIGHT}px`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: 'linear-gradient(135deg, #f0f2f5, #e4e7eb)',
                                  color: '#6d7175',
                                  fontSize: 24,
                                  fontWeight: 600,
                                }}
                              >
                                Add Scene URL to preview
                              </div>
                            )}
                            <div style={previewOverlayStyle}>{previewText || 'Type kid name to preview cover'}</div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {isCoverPreviewScene && generatedTextPreviewUrl ? (
                    <div style={{ border: '1px solid #d8dbde', borderRadius: 8, padding: 8, background: '#fff' }}>
                      <BlockStack gap="100">
                        <Text as="p" variant="bodySm" tone="subdued">Generated Preview (Server Render)</Text>
                        <img
                          src={generatedTextPreviewUrl}
                          alt="Generated scene preview"
                          style={{ width: '100%', borderRadius: 6, display: 'block' }}
                        />
                      </BlockStack>
                    </div>
                  ) : null}
                </BlockStack>
              </div>
            ) : null}
          </div>
        )}
        {getSceneTypeFields(editGenerationSceneData.type).showMaskUrl && !isPromptStory && (
          <>
            <TextField
              label="Mask URL"
              value={editGenerationSceneData.mask_url}
              onChange={(value) => setEditGenerationSceneData(prev => ({ ...prev, mask_url: value, mask_url_2: value }))}
              placeholder="https://example.com/mask.png"
              required
            />
            <div style={{ border: '1px solid #e3e3e3', borderRadius: 8, padding: 12 }}>
              <BlockStack gap="200">
                <Text as="p" variant="bodySm" tone="subdued">Replace Mask Image (uploads to Azure and updates Mask URL)</Text>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(event) => {
                    const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
                    setMaskAssetFile(file);
                  }}
                  style={fileInputStyle}
                />
                <InlineStack gap="200" align="space-between">
                  <Text as="p" variant="bodySm" tone="subdued">
                    {maskAssetFile ? `Selected: ${maskAssetFile.name}` : 'No file selected'}
                  </Text>
                  <Button
                    size="slim"
                    onClick={() => handleUploadAsset('mask')}
                    loading={isUploadingMaskAsset}
                    disabled={!maskAssetFile || isUploadingSceneAsset || isUpdatingGenerationScene}
                  >
                    Upload Mask Image
                  </Button>
                </InlineStack>
              </BlockStack>
            </div>
          </>
        )}
        {getSceneTypeFields(editGenerationSceneData.type).showBlurred && (
          <Checkbox
            label="Blurred"
            checked={editGenerationSceneData.blurred}
            onChange={(checked) => setEditGenerationSceneData(prev => ({ ...prev, blurred: checked }))}
            required
          />
        )}
        {getSceneTypeFields(editGenerationSceneData.type).showDemoText && (
          <TextField
            label="Demo Text"
            value={editGenerationSceneData.demo_text}
            onChange={(value) => setEditGenerationSceneData(prev => ({ ...prev, demo_text: value }))}
            placeholder="Demo text for this scene"
            required={getSceneTypeFields(editGenerationSceneData.type).demoTextRequired}
          />
        )}
      </BlockStack>
    </Modal>
  );
};

export default EditGenerationSceneModal; 
